import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Loader2, PieChart, TrendingUp, Wallet, Activity, User, Phone, Mail, MapPin } from "lucide-react";
import { toTitleCase } from "../../lib/formatName";

const fmtINR = (n) => "₹" + new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const fmtPct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${(n).toFixed(2)}%`);

export default function ClientDashboard() {
  const { api, user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [sb, setSb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!user?.pan_no) { setLoading(false); return; }
      try {
        const [p, m] = await Promise.all([
          api.get(`/portfolio/${user.pan_no}`).catch(() => ({ data: null })),
          api.get("/subbroker/client/my-sub-broker").catch(() => ({ data: { sub_broker: null } })),
        ]);
        setPortfolio(p.data);
        setSb(m.data?.sub_broker);
      } finally { setLoading(false); }
    })();
  }, [api, user]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;
  }

  const summary = (() => {
    const holdings = Array.isArray(portfolio) ? portfolio : (portfolio?.holdings || []);
    return {
      total_current_value: holdings.reduce((s, h) => s + (h.market_value || 0), 0),
      total_cost: holdings.reduce((s, h) => s + (h.cost_value || 0), 0),
    };
  })();
  const topHoldings = (Array.isArray(portfolio) ? portfolio : (portfolio?.holdings || []))
    .filter((h) => (h.market_value || 0) > 0)
    .map((h) => ({
      scheme_name: h.scheme_name,
      units: h.units,
      cost_value: h.cost_value,
      current_value: h.market_value,
    }))
    .sort((a, b) => (b.current_value || 0) - (a.current_value || 0))
    .slice(0, 8);

  const totalCurrent = summary.total_current_value || 0;
  const totalCost = summary.total_cost || 0;
  const totalGain = totalCurrent - totalCost;
  const gainPct = totalCost > 0 ? (totalGain / totalCost) * 100 : null;

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Welcome, {user?.name || user?.pan_no}</h1>
        <p className="text-sm text-gray-500">Your personal portfolio dashboard</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Portfolio Value" value={fmtINR(totalCurrent)} icon={Wallet} accent="bg-emerald-50 text-emerald-700" big />
        <KPI label="Invested" value={fmtINR(totalCost)} icon={TrendingUp} accent="bg-blue-50 text-blue-700" big />
        <KPI label="Gain/Loss" value={fmtINR(totalGain)} sub={fmtPct(gainPct)} icon={Activity}
             accent={totalGain >= 0 ? "bg-green-50 text-green-700" : "bg-rose-50 text-rose-700"}
             valueColor={totalGain >= 0 ? "text-green-700" : "text-rose-700"} big />
        <KPI label="Holdings" value={topHoldings.length} icon={PieChart} accent="bg-indigo-50 text-indigo-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2" data-testid="client-top-holdings">
          <h3 className="font-semibold text-gray-800 mb-3">Top Holdings</h3>
          {topHoldings.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No active holdings yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-500 border-b">
                  <tr>
                    <th className="text-left py-2">Scheme</th>
                    <th className="text-right py-2">Units</th>
                    <th className="text-right py-2">Cost</th>
                    <th className="text-right py-2">Value</th>
                    <th className="text-right py-2">Gain</th>
                  </tr>
                </thead>
                <tbody>
                  {topHoldings.map((h, i) => {
                    const gain = (h.current_value || 0) - (h.cost_value || 0);
                    return (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2">{toTitleCase(h.scheme_name) || h.scheme_name}</td>
                        <td className="py-2 text-right font-mono text-xs">{(h.units || 0).toFixed(2)}</td>
                        <td className="py-2 text-right">{fmtINR(h.cost_value)}</td>
                        <td className="py-2 text-right font-medium">{fmtINR(h.current_value)}</td>
                        <td className={`py-2 text-right font-medium ${gain >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(gain)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Link to="/portfolio" className="text-sm text-teal-600 hover:underline mt-3 inline-block">View full portfolio →</Link>
        </Card>

        <Card className="p-5" data-testid="client-sub-broker-card">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" /> Your Sub Broker
          </h3>
          {sb ? (
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold text-gray-800">{sb.sub_broker_name}</p>
                <p className="text-xs text-gray-500">Code: {sb.sub_broker_code}</p>
              </div>
              {sb.mobile && <div className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5" /> {sb.mobile}</div>}
              {sb.email && <div className="flex items-center gap-2 text-gray-600"><Mail className="w-3.5 h-3.5" /> {sb.email}</div>}
              {sb.arn_code && <div className="flex items-center gap-2 text-gray-600"><span className="font-mono text-xs">ARN:</span> {sb.arn_code}</div>}
              {sb.branch_code && <div className="flex items-center gap-2 text-gray-600"><MapPin className="w-3.5 h-3.5" /> Branch {sb.branch_code}</div>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No Sub Broker assigned. Reach out to Kinntegra support.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent, valueColor, big }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      </div>
      <p className={`${big ? "text-xl" : "text-3xl"} font-bold ${valueColor || "text-gray-800"}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${valueColor || "text-gray-500"}`}>{sub}</p>}
    </Card>
  );
}
